import { nanoid } from 'nanoid';

import type {
  CreateSafetyLogFormValues,
  SafetyLog,
  SafetyLogItem,
  UpdateSafetyLogFormValues,
} from '@/store/safety-log/safety-log.dto';

import type { SafetyItem, SafetyLogState } from './types';

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

// The backend item exposes a single temporal field (`date`) typed as an RFC3339
// timestamp. The UI captures only a time-of-day, so we anchor it to the log's date.
const timeToIso = (logDate: string, time: string): string => {
  const match = TIME_RE.exec(time.trim());
  if (!match) return '';
  const base = new Date(logDate);
  if (Number.isNaN(base.getTime())) return '';
  base.setUTCHours(Number(match[1]), Number(match[2]), 0, 0);
  return base.toISOString();
};

const isoToTime = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return TIME_RE.test(iso) ? iso : '';
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const itemToDto = (item: SafetyItem, logDate: string): SafetyLogItem => ({
  item: item.name,
  remarksCompliance: item.remarksCompliance,
  date: timeToIso(logDate, item.time),
  expiration: item.expiration,
});

// A saved log carries item names and nothing else, so a row read back has no
// equipment id — and nothing left to create, whatever it was added through.
const itemFromDto = (item: SafetyLogItem): SafetyItem => ({
  id: nanoid(),
  name: item.item,
  equipmentId: '',
  isNewEquipment: false,
  remarksCompliance: item.remarksCompliance,
  time: isoToTime(item.date),
  expiration: item.expiration,
});

export const safetyLogToFormState = (log: SafetyLog): SafetyLogState => ({
  date: log.date,
  items: log.items.map(itemFromDto),
});

/**
 * Seed a brand-new log from a previous one: carry over only the item names
 * (the checklist), leaving remarks and time blank for the new day's entry.
 */
export const safetyLogToTemplateFormState = (log: SafetyLog, date: string): SafetyLogState => ({
  date,
  items: log.items.map((item) => ({
    id: nanoid(),
    name: item.item,
    equipmentId: '',
    isNewEquipment: false,
    remarksCompliance: '',
    time: '',
    expiration: '',
  })),
});

export const formStateToCreateValues = (state: SafetyLogState): CreateSafetyLogFormValues => ({
  date: state.date,
  items: state.items.map((item) => itemToDto(item, state.date)),
});

export const formStateToUpdateValues = (
  id: string,
  state: SafetyLogState,
): UpdateSafetyLogFormValues => ({
  id,
  items: state.items.map((item) => itemToDto(item, state.date)),
});
